import type { EChartsOption } from 'echarts';

export const DASHBOARD_CHART_FONT = 'Lexend, system-ui, -apple-system, sans-serif';

function isDark(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
}

const LIGHT = {
  brand: '#6C47FF',
  ok: '#10B981',
  warn: '#F59E0B',
  info: '#2563EB',
  danger: '#EF4444',
  text: '#111827',
  textMuted: '#6B7280',
  border: '#E5E7EB',
  track: 'rgba(108, 71, 255, 0.08)',
  chartOpt: '#14B8A6',
  chartApp: '#3B82F6',
  chartOff: '#F59E0B',
};

const DARK = {
  brand: '#6C47FF',
  ok: '#34D399',
  warn: '#FBBF24',
  info: '#60A5FA',
  danger: '#F87171',
  text: '#E5E7EB',
  textMuted: '#9CA3AF',
  border: 'rgba(255,255,255,0.08)',
  track: 'rgba(108, 71, 255, 0.18)',
  chartOpt: '#5EEAD4',
  chartApp: '#7DD3FC',
  chartOff: '#FCD34D',
};

function cssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const val = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return val || fallback;
}

export function getDashboardChartColors() {
  const fb = isDark() ? DARK : LIGHT;
  return {
    brand: cssVar('--ds-brand', fb.brand),
    ok: cssVar('--ds-ok', fb.ok),
    warn: cssVar('--ds-warn', fb.warn),
    info: cssVar('--ds-info', fb.info),
    danger: cssVar('--ds-danger', fb.danger),
    text: cssVar('--ds-text-strong', fb.text),
    textMuted: cssVar('--ds-text-muted', fb.textMuted),
    border: cssVar('--ds-border', fb.border),
    track: cssVar('--ds-brand-soft', fb.track),
    chartOpt: cssVar('--ds-chart-opt', fb.chartOpt),
    chartApp: cssVar('--ds-chart-app', fb.chartApp),
    chartOff: cssVar('--ds-chart-off', fb.chartOff),
  };
}

export function baseChartOption(): Pick<EChartsOption, 'textStyle' | 'animation'> {
  return {
    textStyle: { fontFamily: DASHBOARD_CHART_FONT },
    animationDuration: 650,
    animationEasing: 'cubicOut',
  };
}

/** Tooltip colors that contrast with the dashboard surface (not chart axis text). */
export function chartTooltipOption(
  theme: 'light' | 'dark',
  overrides: Partial<NonNullable<EChartsOption['tooltip']>> = {},
): NonNullable<EChartsOption['tooltip']> {
  const colors = getDashboardChartColors();
  const dark = theme === 'dark';

  const base: NonNullable<EChartsOption['tooltip']> = dark
    ? {
        backgroundColor: 'rgba(17, 24, 39, 0.94)',
        borderColor: colors.border,
        textStyle: {
          color: '#F9FAFB',
          fontSize: 11,
          fontFamily: DASHBOARD_CHART_FONT,
        },
        extraCssText: 'box-shadow: 0 4px 16px rgba(0,0,0,0.35);',
      }
    : {
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: 'rgba(17, 24, 39, 0.12)',
        textStyle: {
          color: '#111827',
          fontSize: 11,
          fontFamily: DASHBOARD_CHART_FONT,
        },
        extraCssText: 'box-shadow: 0 4px 14px rgba(17,24,39,0.12);',
      };

  return { ...base, ...overrides };
}

export function scoreColor(pct: number, colors = getDashboardChartColors()): string {
  if (pct >= 75) return colors.ok;
  if (pct >= 55) return colors.warn;
  if (pct > 0) return colors.brand;
  return colors.textMuted;
}
